<script lang="ts">
  // eslint-disable-next-line import-x/no-duplicates
  import { onMount } from 'svelte';
  // eslint-disable-next-line import-x/no-duplicates
  import { fly } from 'svelte/transition';
  import { Canvas } from '@threlte/core';
  import { useProgress } from '@threlte/extras';
  import type { HomepageHardwareScrollProps } from './types';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';
  import { lerp } from 'three/src/math/MathUtils.js';
  import WebGL from 'three/addons/capabilities/WebGL.js';
  import layoutHelpers from '$lib/helpers/layout-helpers';
  import { checkSrc } from '$lib/helpers/asset-verification-helpers';
  import PerformanceMonitor from '$components/common/partials/PerformanceMonitor.svelte';
  import Scene from './ThrelteScene.svelte';
  import Fallback from './Fallback.svelte';

  // #region Props
  type $$Props = HomepageHardwareScrollProps;

  export let entryId: HomepageHardwareScrollProps['entryId'];
  export let sectionContent: HomepageHardwareScrollProps['sectionContent'];
  export let bgImgLayouts: HomepageHardwareScrollProps['bgImgLayouts'];
  export let imgSequenceConfig: HomepageHardwareScrollProps['imgSequenceConfig'];
  export let scene: HomepageHardwareScrollProps['scene'];
  export let bankingDisclaimer: HomepageHardwareScrollProps['bankingDisclaimer'];
  export let fallbackImgUpgradeDelay: HomepageHardwareScrollProps['fallbackImgUpgradeDelay'];
  export let enableQueriesOnProd: HomepageHardwareScrollProps['enableQueriesOnProd'];

  // #endregion

  // #region Setup
  const params = {
    cameraMouseSpeed: 0.1,
    lerpSpeed: 0.025,
    scrollLerpSpeed: 0.1,
    transitionDuration: 500,
    imageDelay: 100,
    reduceMotion: false,
  };

  const {
    active, // Readable<boolean> – if the DefaultLoadingManager is active
  } = useProgress();

  let windowHeight = 2048;
  let w: number;
  let h: number;
  let y: number = 0;
  let smoothY = y;
  let progress = 0;
  const delayPercentage = 0;
  let threlteScene: HTMLElement;
  const totalSections = sectionContent.length + 1;

  const MOBILE_BREAKPOINT = layoutHelpers.BREAKPOINT_WIDTHS.medium.minWidth;

  $: isMobile = w < MOBILE_BREAKPOINT;
  $: isLandscape = w > h;
  $: videoSection = Math.min(Math.max(Math.floor((y - windowHeight) / windowHeight), 0), totalSections);

  $: section = isRender3d ? Math.min(threlteSection, totalSections) : Math.min(videoSection, totalSections);
  $: sceneTop = threlteScene ? threlteScene.offsetTop : windowHeight;
  $: threlteSection =
    Math.max(Math.floor((smoothY - sceneTop) / windowHeight), 0) +
    (isMobile ? Math.max(Math.min(Math.floor((smoothY - sceneTop) / windowHeight), 1), 0) : 0);

  $: sectionProgress = (smoothY - sceneTop) / windowHeight - section;

  let lastSection = false;
  let hardwareContainer: HTMLElement;
  let isTabbing = false;
  const sectionElements: HTMLElement[] = [];

  $: lastSection = section >= totalSections;

  // #endregion

  // #region 3D & Performance
  const flipflops = 2;
  let pixelRatio: number;
  let pixelRatioDropoff: number;

  let triggerFallback = () => {};
  let isShowPerformanceMonitor = false;

  let isRender3d = true;
  $: {
    if (!isRender3d) {
      triggerFallback();
    }
  }

  let isPreserveDrawingBuffer = false; // when true, images can be captured from canvas (has performance cost)
  $: rendererParameters = {
    preserveDrawingBuffer: isPreserveDrawingBuffer,
  };

  const validateSceneResources = () => {
    const resources = [
      { urls: scene.materials, type: 'textures' },
      { urls: scene.models, type: 'models' },
    ];
    return resources.every(({ urls, type }) => Object.values(urls).every((url) => checkSrc(url, type)));
  };

  // check for webgl2 support
  isRender3d = WebGL.isWebGL2Available();
  // check model url validity
  isRender3d = isRender3d && validateSceneResources();
  // #endregion

  // #region Handler
  function handleResize() {
    windowHeight = window.innerHeight;
  }

  function handleDownscale() {
    if (!pixelRatioDropoff) {
      pixelRatioDropoff = (pixelRatio - 1) / flipflops;
    }
    pixelRatio -= pixelRatioDropoff;
  }

  function handleFallback() {
    isRender3d = false;
  }
  // #endregion

  // #region Lifecycle
  const getUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return Object.fromEntries(urlParams.entries());
  };

  const updatePropsFromParams = () => {
    const hostname = window.location.hostname;

    // Regex pattern to match 'xms-fe-*-playpen-*--xms-fe.stage.sqprod.co'
    const stageRegex = /^xms-fe-[^-]+-playpen-[^-]+--xms-fe\.stage\.sqprod\.co$/;

    const allowedHosts = ['localhost', 'squareupstaging.com']; // only allow our queries to work in dev and staging
    if (enableQueriesOnProd) {
      allowedHosts.push('squareup.com');
    }

    // Check if the hostname matches any allowed hosts or the stage regex
    if (allowedHosts.includes(hostname) || stageRegex.test(hostname)) {
      const urlParams = getUrlParams();
      if (urlParams.isRender3d !== undefined) {
        isRender3d = urlParams.isRender3d === 'true';
      }
      if (urlParams.isShowPerformanceMonitor !== undefined) {
        isShowPerformanceMonitor = urlParams.isShowPerformanceMonitor === 'true';
      }
      if (urlParams.isPreserveDrawingBuffer !== undefined) {
        isPreserveDrawingBuffer = urlParams.isPreserveDrawingBuffer === 'true';
      }
    }
  };

  let scrollLoop: number;
  function smoothScroll() {
    smoothY = lerp(smoothY, y, params.scrollLerpSpeed);
    scrollLoop = requestAnimationFrame(smoothScroll);
    progress = Number(lerp(progress, percentageSeen(threlteScene), params.scrollLerpSpeed).toFixed(5));
  }

  // Calculates the "percentage seen" based on when the image first enters the screen until the moment it leaves
  function percentageSeen(element: HTMLElement) {
    const canvasPosY = element.getBoundingClientRect().top + smoothY;
    const distance = smoothY + windowHeight - canvasPosY;
    let percentage = distance / ((windowHeight + threlteScene.scrollHeight) / 100);
    percentage = Math.max(0, Math.min(100, percentage - delayPercentage));

    return percentage;
  }

  onMount(() => {
    params.reduceMotion = window.matchMedia('prefers-reduced-motion: reduce').matches;
    windowHeight = window.innerHeight;
    updatePropsFromParams();
    smoothScroll();
    addFocusListeners();
    percentageSeen(threlteScene);

    return () => {
      cancelAnimationFrame(scrollLoop);
      removeFocusListeners();
    };
  });
  // #endregion

  function showSection(event: FocusEvent) {
    if (!isTabbing) {
      return;
    }

    const target = event.currentTarget as HTMLElement;
    const sectionIndex = sectionElements.findIndex((el) => el.contains(target));
    const newSection = sectionIndex + 2;

    hardwareContainer.scrollTo({ top: 0, behavior: 'smooth' });

    // Adjust y based on whether it's mobile
    y = (newSection + 1) * windowHeight + (isMobile ? -1 : 1);
    smoothY = y;
  }

  function handleKeyDown(event: KeyboardEvent) {
    isTabbing = event.key === 'Tab';
  }

  function handleMouseDown() {
    isTabbing = false;
  }

  function addFocusListeners() {
    sectionElements.forEach((section) => {
      const anchor = section.querySelector('a') as HTMLAnchorElement;
      anchor.addEventListener('focus', showSection);
    });
  }

  function removeFocusListeners() {
    sectionElements.forEach((section) => {
      const anchor = section.querySelector('a') as HTMLAnchorElement;
      anchor.removeEventListener('focus', showSection);
    });
  }
</script>

<svelte:window on:mousedown={handleMouseDown} on:keydown={handleKeyDown} bind:scrollY={y} on:resize={handleResize} />
<PerformanceMonitor
  bind:triggerFallback
  isDisplayGui={isShowPerformanceMonitor}
  {flipflops}
  onDecline={handleDownscale}
  onFallback={handleFallback}
/>
<div
  class="HomepageHardwareScroll HomepageHardwareScroll-{entryId}-box-properties"
  data-entry-id={entryId}
  style="--sections: {totalSections + 1}"
>
  <div class="threlte" class:fallback={!isRender3d} bind:this={threlteScene}>
    <div class="threlte-container" bind:clientWidth={w} bind:clientHeight={h}>
      <div class="hardware-sections">
        {#if !isRender3d}
          <Fallback
            {imgSequenceConfig}
            {fallbackImgUpgradeDelay}
            {windowHeight}
            {y}
            {isMobile}
            section={videoSection}
          />
        {/if}

        {#each sectionContent as content, index}
          {#if section - 2 === index || (index === 4 && lastSection)}
            <div
              class={`hardware-images layout-${bgImgLayouts[index]}`}
              in:fly={{
                duration: params.transitionDuration,
                delay: params.imageDelay,
                y: 100 * (params.reduceMotion ? 0.2 : 1),
              }}
              out:fly={{
                duration: params.transitionDuration / 2,
                y: -100 * (params.reduceMotion ? 0.2 : 1),
              }}
            >
              {#each content.imageContainers as imageContainer}
                <div class="hardware-image">
                  <DynamicChildrenComponent children={[imageContainer]} />
                </div>
              {/each}
            </div>
          {/if}
        {/each}
        {#if isRender3d}
          <div class="threlte-scene">
            <div class="threlte-scene-loader" class:isLoading={$active} />
            {#key rendererParameters}
              <Canvas {...rendererParameters}>
                <Scene
                  {section}
                  sectionPercentage={sectionProgress}
                  {isMobile}
                  {isLandscape}
                  {scene}
                  {params}
                  bind:pixelRatio
                />
              </Canvas>
            {/key}
          </div>
        {/if}
      </div>
      <div class="hardware-section-container" bind:this={hardwareContainer}>
        {#each sectionContent as content, index}
          <div
            class="hardware-section"
            class:is-visible={section - 2 === index || (index === 4 && lastSection)}
            bind:this={sectionElements[index]}
          >
            <div class="hardware-section-copy" class:fallback={!isRender3d}>
              <div class="hardware-section-headline">
                <DynamicChildrenComponent children={[content.headline]} />
                <DynamicChildrenComponent children={[content.link]} />
              </div>
            </div>
            {#if index === 4 && bankingDisclaimer}
              <div class="hardware-section-disclaimer hide-until-medium">
                <DynamicChildrenComponent children={[bankingDisclaimer]} />
              </div>
            {/if}
          </div>
          <div
            class="hardware-section-spacer"
            class:is-visible={section - 2 === index || (index === 4 && lastSection)}
          />
        {/each}
      </div>
    </div>
  </div>
</div>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/core/config/_media_queries' as mediaQueries;
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/variables.scss' as vars;

  :global(body) {
    --navHeight: 69px;
    background: #fffdfa;
  }
  :global(.PublicWebNav nav.DesktopNav, .PublicWebNav nav.MobileNav) {
    --nav-outer-gutter-base: clamp(16px, 5vw, 40px);
    background-color: #fffdfa !important;
    border-color: transparent !important;
  }

  :global(.PublicWebNav nav.MobileNav, .PublicWebNavV2 .GlobalNav .MobileNav, .PublicWebNavV2 .GlobalNav .DesktopNav) {
    background-color: #fffdfa !important;
    border-color: transparent !important;
  }

  :global(.PublicWebNav .CtaSubnav .header, .PublicWebNavV2 .CtaSubnav .header) {
    --color-background: #fffdfa;
    box-shadow: none !important;
    border: none !important;
  }

  :global(.PublicWebNav .CtaSubnav .header) {
    display: none;
  }

  :global(.PublicWebNav .CtaSubnavDesktopNav .left-slot, .PublicWebNavV2 .CtaSubnavDesktopNav .left-slot) {
    align-content: center;
  }

  :global {
    .PublicWebNavV2 .CtaSubnav .footer,
    .PublicWebNav .CtaSubnav .footer {
      --color-background: #fffdfa;
      border: none;
      bottom: auto;
      box-shadow: none;
      transform: translateY(-100%);
      position: fixed;
      top: 0;
      display: flex;

      &.isVisible {
        transform: translateY(0) !important;
        @include mediaQueries.until('medium') {
          display: grid;
          grid-template-columns: 1fr 1fr;
        }
      }

      @include mediaQueries.until('large') {
        &.disableLeftCtaIcon {
          flex-direction: row;
        }
      }

      @include mediaQueries.at('large') {
        display: none;
      }
    }
  }

  :global(.BasePage .StickyBannerSection) {
    background-color: #fffdfa;
    box-shadow: none;
    padding: var(--space-10) clamp(16px, 5vw, 40px);
  }

  :global(.sq-custom-disclaimer-link a) {
    color: #959595;
    font-weight: 400;
  }

  .hardware-sections {
    display: flex;
    flex-direction: column;
    height: 100dvh;
    left: 0;
    justify-content: center;
    overflow: clip;
    pointer-events: none;
    position: absolute;
    top: 0;
    width: 100%;
  }

  .hardware {
    &-section {
      --navHeight: 69px;
      --sectionSpacing: 32px;

      display: grid;
      column-gap: var(--space-10);
      grid-template-columns: repeat(8, 1fr);
      height: 100dvh;
      max-width: 1920px;
      left: 50%;
      padding-bottom: var(--sectionSpacing);
      padding-left: var(--space-40);
      padding-top: var(--navHeight);
      top: 0;
      transform: translateX(-50%);
      width: 100%;
      z-index: 100;

      @media (min-width: vars.$breakpoint-width-small) {
        grid-template-columns: repeat(12, 1fr);
      }

      &.hide-at-medium {
        @media (min-width: vars.$breakpoint-width-small) {
          display: none;
        }
      }

      &.is-visible {
        position: absolute;
        padding-left: 0;
        top: 0;
        width: calc(100% - var(--space-20));
        @media (min-width: vars.$breakpoint-width-small) {
          width: calc(100% - var(--space-80));
        }

        .hardware-section-copy,
        .hardware-section-disclaimer {
          opacity: 1;
          transform: translateY(0);
          transition: all 0.4s cubic-bezier(0.42, 0, 0.58, 1);
        }

        .hardware-section-disclaimer {
          align-self: flex-end;
        }
      }

      @media (min-width: vars.$breakpoint-width-small) {
        justify-content: flex-start;
      }

      &-container {
        left: 0;
        position: absolute;
        width: 100%;
      }

      // Copy
      &-copy {
        bottom: 20px;
        grid-column: 1;
        position: absolute;
        opacity: 0;
        pointer-events: all;
        transform: translateY(var(--space-40));

        @media (prefers-reduced-motion) {
          transform: translateY(0px);
        }

        @media (min-width: calc(vars.$breakpoint-width-small)) {
          grid-column: 1 / -1;
          position: static;
        }

        &.fallback {
          //adjust fallback copy position for shorter screens
          @media screen and (max-height: 667px) and (orientation: portrait) {
            bottom: 20px;
          }
        }
      }

      &-disclaimer {
        grid-column: 1 / -1;
        opacity: 0;
        transform: translateY(var(--space-40));

        @media (min-width: vars.$breakpoint-width-small) {
          grid-column: 10 / -1;
        }

        &.hide-until-medium {
          @media (max-width: vars.$breakpoint-width-small) {
            display: none;
          }
        }

        :global {
          p {
            font-size: inherit;
            line-height: var(--line-height-18);
            text-align: left;
            text-wrap: balance;

            span {
              line-height: 0;
            }
          }
        }
      }

      // Headline
      &-headline {
        max-width: 42rem;

        :global {
          h2 {
            margin-bottom: 3rem;

            text-wrap: balance;
            font-size: clamp(2.4rem, 7vw, 4.8rem);
            font-weight: var(--font-weight-regular);
            line-height: clamp(2.6rem, 7.25vw, 5.2rem);

            span {
              display: block;
            }
          }
        }
      }

      // CTA
      :global {
        .LinkComponent {
          .arrow {
            span {
              &::after {
                content: '→';
                display: inline-flex;
                align-items: center;
                aspect-ratio: 1 / 1;
                margin-left: 0.625em;
                padding: 0.6rem 0.8rem;
                background-color: var(--color-blue);
                border-radius: 10rem;
                color: white;
                line-height: 1;
                height: 26px;
                width: 26px;
                justify-content: center;

                @media (min-width: vars.$breakpoint-width-medium) {
                  height: 30px;
                  width: 30px;
                }
              }
            }
          }
        }
      }

      &-spacer {
        display: none;
        height: 120vh;
        left: 50%;
        padding-bottom: var(--sectionSpacing);
        padding-top: calc(var(--navHeight) + var(--sectionSpacing));
        transform: translateX(-50%);
        top: 0;
        width: calc(100% - var(--space-20));
        @media (min-width: vars.$breakpoint-width-small) {
          width: calc(100% - var(--space-80));
        }
        &.is-visible {
          display: block;
        }
      }
    }

    &-images {
      --navHeight: 69px;
      --sectionSpacing: clamp(10px, 3vh, 75px);

      column-gap: var(--space-10);
      display: grid;
      grid-template-columns: repeat(8, 1fr);
      height: 100%;
      left: 50%;
      max-width: 1920px;
      padding-bottom: var(--sectionSpacing);
      padding-top: calc(var(--navHeight) + var(--sectionSpacing));
      pointer-events: all;
      position: absolute;
      transform: translateX(-50%);
      top: 0;
      z-index: -1;
      width: calc(100% - var(--space-20));

      @media (min-width: vars.$breakpoint-width-small) {
        width: calc(100% - var(--space-80));
        grid-template-columns: repeat(12, 1fr);
      }

      &.layout-1 .hardware-image {
        &:nth-child(1) {
          justify-self: flex-start;
          grid-column: 6 / span 3;

          @media (min-width: vars.$breakpoint-width-small) {
            grid-column: 2 / span 3;
            align-self: flex-end;
          }
        }

        &:nth-child(2) {
          grid-column: 1 / span 5;
          justify-self: flex-end;

          @media (min-width: vars.$breakpoint-width-small) {
            grid-column: 9 / span 3;
          }
        }
      }

      &.layout-2 .hardware-image {
        &:nth-child(1) {
          grid-column: 4 / span 5;
          justify-self: flex-start;
          @media (min-width: vars.$breakpoint-width-small) {
            grid-column: 2 / span 4;
          }
        }

        &:nth-child(2) {
          grid-column: 1 / span 3;
          justify-self: flex-end;
          @media (min-width: vars.$breakpoint-width-small) {
            grid-column: 10 / span 2;
          }
        }
      }
    }

    &-image {
      :global {
        img {
          border-radius: 12px;
          margin-top: var(--space-10);
          max-width: 100%;
          object-fit: cover;

          @media (min-width: vars.$breakpoint-width-small) {
            margin-bottom: var(--space-10);
            max-height: clamp(150px, 28vmin, 400px);
            max-width: clamp(240px, 30vw, 440px);
          }

          @media (min-width: vars.$breakpoint-width-extra-large) {
            max-height: clamp(150px, 40vmin, 600px);
            max-width: clamp(240px, 30vw, 640px);
          }
        }

        p {
          font-family: var(--font-family-body);
          font-size: var(--font-size-12);
          line-height: 1.16667;

          strong {
            font-weight: var(--font-weight-semibold);
          }

          @media (min-width: vars.$breakpoint-width-small) {
            font-size: var(--font-size-body);
          }
        }

        .BoxComponent {
          display: flex;
          flex-direction: column-reverse;

          @media (min-width: vars.$breakpoint-width-small) {
            flex-direction: column;
          }
        }
      }

      &:nth-child(1) {
        order: 1;
        margin-bottom: clamp(var(--space-60), 3vw, var(--space-80));
        text-align: right;

        @media (min-width: vars.$breakpoint-width-small) {
          align-self: flex-end;
          order: 0;
          text-align: left;
        }
      }

      &:nth-child(2) {
        grid-column: 1 / span 4;
        margin-top: var(--space-80);
        text-align: left;

        @media (min-width: vars.$breakpoint-width-small) {
          grid-column: 9 / span 4;
          margin-top: 0;
          order: 1;
          text-align: right;
        }
      }
    }
  }

  .threlte {
    height: calc((100vh * var(--sections)) + 50vh);
    position: relative;

    &-container {
      height: 100vh;
      position: sticky;
      top: 0;
      z-index: 0;
    }

    &-scene {
      position: absolute;
      height: 100vh;
      width: 100vw;

      &-loader {
        --iconSize: 48px;
        animation: spin 1.5s infinite linear;
        background-image: url("data:image/svg+xml,%3Csvg width='48' height='48' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg id='Radial spinner'%3E%3Cpath id='Vector' fill-rule='evenodd' clip-rule='evenodd' d='M19 12C19 8.13401 15.866 5 12 5V3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12H5C5 15.866 8.13401 19 12 19C15.866 19 19 15.866 19 12Z' fill='%237f7d7a'/%3E%3C/g%3E%3C/svg%3E%0A");
        height: var(--iconSize);
        left: 50%;
        pointer-events: none;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        transition: opacity var(--duration-default) var(--easing-default);
        width: var(--iconSize);
        opacity: 0;
        visibility: hidden;

        &.isLoading {
          opacity: 1;
          visibility: visible;
          pointer-events: all;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      }
    }

    canvas {
      position: absolute;
      pointer-events: none;
    }

    @media (min-width: 1024px) {
      // 80vh accounts for 20vh margin bottom from hero,
      height: calc((100vh * var(--sections)) + 80vh + var(--navHeight));
    }
  }

  :global {
    .hardware-image {
      &:nth-child(2) {
        & > .BoxComponent {
          display: flex;
          flex-direction: column-reverse;

          @media screen and (min-width: vars.$breakpoint-width-small) {
            display: block;
          }
        }
      }
    }
  }

  @include mediaQueries.until('medium') {
    @media (min-aspect-ratio: 8/5) {
      .threlte-scene {
        transform: scale(0.8);
        transform-origin: center bottom;
      }

      .hardware-section-headline {
        max-width: 17rem;
        :global {
          h2 {
            font-size: 20px;
            line-height: 25px;
          }
        }
      }
      .hardware-images {
        --sectionSpacing: 0px;
        grid-template-columns: repeat(14, 1fr);
        grid-gap: var(--space-10);

        .hardware-image {
          width: 100%;
        }

        :global {
          p {
            margin-top: 0;
          }
          img {
            margin: 0;
          }
        }
        &.layout-1 .hardware-image:nth-child(1),
        &.layout-2 .hardware-image:nth-child(2) {
          grid-column: 10 / span 5;
          grid-row: 1;
          :global {
            img {
              aspect-ratio: 1;
              object-position: top;
            }
            .BoxComponent {
              display: flex;
              flex-direction: row-reverse;
              align-items: flex-end;
              gap: 8px;
              .AproposImageComponent {
                flex-basis: 60%;
                max-width: 30vh;
              }
              .TextUnitComponent {
                flex-basis: 40%;
                text-align: right;
              }
            }
          }
        }

        &.layout-1 .hardware-image:nth-child(2),
        &.layout-2 .hardware-image:nth-child(1) {
          grid-column: 9 / span 6;
          grid-row: 2;

          :global {
            .BoxComponent {
              display: flex;
              flex-direction: row-reverse;
              align-items: flex-end;
              text-align: right;
              gap: 8px;
            }
          }
        }
        &.layout-1 .hardware-image {
          margin: 0;

          &:nth-child(1) {
            .BoxComponent {
              position: relative;

              @media (max-width: vars.$breakpoint-width-small) {
                .TextUnitComponent {
                  position: absolute;
                  top: 0;
                  right: calc(100% + var(--space-10));
                  width: 100%;
                }

                img {
                  margin-top: 0;
                }
              }
            }
          }
        }

        &.layout-2 .hardware-image {
          margin: 0;
          &:nth-child(1) {
            grid-row: 1;
            :global {
              .BoxComponent {
                align-items: flex-start;
              }
            }
          }

          &:nth-child(2) {
            grid-row: 2;
          }
        }
      }
    }

    @media (min-aspect-ratio: 12/5) {
      .hardware-images {
        &.layout-1 .hardware-image {
          &:nth-child(2) {
            grid-column: 9 / span 6;
          }
        }
        &.layout-2 .hardware-image {
          &:nth-child(1) {
            :global {
              .BoxComponent {
                align-items: flex-start;
              }
            }
          }
        }
      }
    }
  }

  @include mediaQueries.until('large') {
    @media (min-aspect-ratio: 4/3) and (orientation: landscape) {
      .hardware-section-headline {
        max-width: 35rem;
        :global {
          h2 {
            font-size: 30px;
            line-height: 35px;
          }
        }
      }
    }
  }

  @media (min-aspect-ratio: 2/1) {
    @include mediaQueries.until('large') {
      @include mediaQueries.at('medium') {
        .hardware-image:first-child {
          display: none;
        }

        .hardware-section-disclaimer {
          :global(.InlineTypographyComponent) {
            font-size: 10px;
            line-height: var(--font-size-12);
          }
        }
      }
    }

    @include mediaQueries.at('large') {
      .hardware-image:last-child {
        display: none;
      }
    }
  }
</style>
